// contexts/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import axios from 'axios';
import Cookies from 'js-cookie';

// Create the context with default values
export const AuthContext = createContext({
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  updateProfile: async () => {},
  resetPassword: async () => {},
  confirmPasswordReset: async () => {},
  verifyEmail: async () => {},
  checkEmailVerificationStatus: async () => {},
  deleteAccount: async () => {},
});

// API URL from environment variable or fallback
const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8081';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  // Initialize auth state from localStorage on component mount
  useEffect(() => {
    try {
      const storedToken = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setToken(storedToken);
          setUser(parsedUser);
          
          // Set axios default headers
          axios.defaults.headers.common['Authorization'] = `Token ${storedToken}`;
        } catch (parseError) {
          // Clear corrupted data
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
        }
      }
    } catch (error) {
      // In case localStorage is completely inaccessible
    }
  }, []);

  // Setup CSRF token handling
  useEffect(() => {
    const csrftoken = Cookies.get('csrftoken');
    if (csrftoken) {
      axios.defaults.headers.common['X-CSRFToken'] = csrftoken;
    }
    
    // Enable withCredentials for all requests to support CSRF cookies
    axios.defaults.withCredentials = true;
  }, []);

  // Login function
  const login = async (username, password) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/wt/api/auth/login/`, {
        username,
        password,
      });
      
      const { token, user } = response.data;
      
      // Store auth data
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Update state
      setToken(token);
      setUser(user);
      
      // Set axios default headers
      axios.defaults.headers.common['Authorization'] = `Token ${token}`;
      
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/wt/api/auth/register/`, userData);
      
      const { token, user } = response.data;
      
      // Store auth data
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Update state
      setToken(token);
      setUser(user);
      
      // Set axios default headers
      axios.defaults.headers.common['Authorization'] = `Token ${token}`;
      
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API_URL}/wt/api/auth/logout/`);
      }
    } catch (error) {
      // Continue with logout even if the API call fails
    } finally {
      // Clear local storage
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      
      // Update state
      setToken(null);
      setUser(null);
      
      // Remove axios default headers
      delete axios.defaults.headers.common['Authorization'];
      
      // Redirect to home/login
      router.push('/login');
    }
  };

  // Update profile function
  const updateProfile = async (userData) => {
    try {
      setLoading(true);
      const response = await axios.patch(`${API_URL}/wt/api/auth/user/update/`, userData);
      
      // Update stored user data
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Password reset function
  const resetPassword = async (email) => {
    try {
      setLoading(true);
      
      // Get the CSRF token from cookies
      const csrftoken = Cookies.get('csrftoken');
      
      // Create a config object with CSRF token and credentials
      const config = {
        headers: {
          'X-CSRFToken': csrftoken
        },
        withCredentials: true
      };
      
      const response = await axios.post(`${API_URL}/wt/api/auth/password/reset/`, { email }, config);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Confirm password reset function
  const confirmPasswordReset = async (uid, token, new_password1, new_password2) => {
    try {
      setLoading(true);
      // Get the CSRF token from cookies
      const csrftoken = Cookies.get('csrftoken');
      
      // Create a config object with CSRF token
      const config = {
        headers: {
          'X-CSRFToken': csrftoken
        },
        withCredentials: true
      };
      
      const response = await axios.post(`${API_URL}/wt/api/auth/password/reset/confirm/`, {
        uid, token, new_password1, new_password2
      }, config);
      
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Email verification function
  const verifyEmail = async (key) => {
    try {
      setLoading(true);
      // Get the CSRF token from cookies
      const csrftoken = Cookies.get('csrftoken');
      
      // Create a config object with CSRF token
      const config = {
        headers: {
          'X-CSRFToken': csrftoken
        },
        withCredentials: true
      };
      
      const response = await axios.post(`${API_URL}/wt/api/auth/email/verify/`, { key }, config);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Check email verification status
  const checkEmailVerificationStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/wt/api/auth/email/verify/status/`);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete account function
  const deleteAccount = async () => {
    try {
      setLoading(true);
      // Get the CSRF token from cookies
      const csrftoken = Cookies.get('csrftoken');
      
      // Create a config object with CSRF token
      const config = {
        headers: {
          'X-CSRFToken': csrftoken
        },
        withCredentials: true
      };
      
      await axios.delete(`${API_URL}/wt/api/auth/user/delete/`, config);
      logout(); // Log out after successful deletion
      return { success: true };
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token,
        loading,
        login,
        register,
        logout,
        updateProfile,
        resetPassword,
        confirmPasswordReset,
        verifyEmail,
        checkEmailVerificationStatus,
        deleteAccount
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => React.useContext(AuthContext);